import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

const listVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.3,
      duration: 0.5,
      ease: "easeInOut",
    },
  }),
};

export default function Profile() {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <section
      id="profile"
      ref={ref}
      className="flex items-center justify-center bg-gray-800 text-white pt-6 pb-8 md:pt-12 md:pb-12 w-full px-4 sm:px-6 md:px-8"
    >
      <div className="w-full max-w-7xl container mx-auto p-4 md:p-12 flex flex-col items-start space-y-4 md:space-y-6 font-ntr">
        <h2 className="text-4xl mb-4 md:mb-5 font-bold text-left w-full md:w-auto">
          <span className="text-custom-accent">/ </span>About Me
        </h2>

        <div className="flex flex-col md:flex-row items-start justify-center w-full space-y-6 md:space-y-0 md:space-x-6">
          {/* Left side - About Me */}
          <div className="md:w-4/5 space-y-4 text-left">
            <p className="text-lg sm:text-lg lg:text-xl mb-4 leading-relaxed text-left text-white font-light">
              I’m a <span className="text-custom-accent font-medium">Software Developer Lead</span> at{" "}
              <a
                href="https://www.chainxy.com/chainxy"
                className="text-custom-accent"
                target="_blank"
                rel="noopener noreferrer"
              >
                ChainXY
              </a>
              , where I design and build large-scale data extraction and
              automation systems that power real estate, retail, and GIS-driven
              insights. Over the past few years, I’ve led projects integrating{" "}
              <span className="text-custom-accent font-medium">Python, React, and C#</span>{" "}
              ecosystems — delivering cleaner data pipelines, faster performance, and smarter automation across multiple teams.
            </p>

            <p className="text-lg sm:text-lg lg:text-xl mb-4 leading-relaxed text-left text-white font-light">
              My focus is on building <span className="text-custom-accent">efficient, maintainable, and intelligent systems</span> —
              combining strong technical execution with thoughtful UX and product awareness.
              I’m currently exploring opportunities to apply my experience in{" "}
              <span className="text-custom-accent">scalable architecture, API development, and AI-assisted engineering</span>{" "}
              within innovative teams.
            </p>

            <h2 className="text-lg text-left font-medium">
              Here are some technologies I’ve been working with recently:
            </h2>
            <ul className="grid grid-cols-2 gap-x-2 gap-y-2 list-inside">
              {[
                "JavaScript (ES6+)",
                "React.js / Next.js",
                "TypeScript",
                "Python (Asyncio, Scrapy, Selenium, Flask)",
                "C# / .NET 6",
                "PostgreSQL / SQL",
              ].map((tech, index) => (
                <motion.li
                  key={tech}
                  className="flex items-center space-x-1 text-left"
                  custom={index}
                  initial="hidden"
                  animate={controls}
                  variants={listVariants}
                >
                  <span className="text-custom-accent">→</span>
                  <span>{tech}</span>
                </motion.li>
              ))}
            </ul>

            <p className="text-lg sm:text-lg lg:text-xl mb-4 leading-relaxed text-left text-white font-light">
              Outside of work, I’m passionate about staying active — whether that’s
              hiking, skiing, or training at the gym. I value balance, creativity,
              and continuous learning — both in life and in code.
            </p>
          </div>

          {/* Right side - Image Card */}
          <div className="hidden md:flex md:w-2/5 justify-center">
            <div className="w-full max-w-xs bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden transition-transform duration-50 ease-in-out transform hover:-translate-y-2">
              <img
                src="/my_profile_image.jpg"
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
